"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  title: "SKIN N' BONEZ - SETLIST",
  description: "Loud riffs, tight grooves, and straight-up rock ’n’ roll energy. Dive into our setlist and get a feel for the live experience.",
  image: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO11.jpg?v=2ua5mkxdr9t"
}, {
  title: "What a night with SKIN N' BONEZ is really like?",
  description: "Hit play and get a taste of the tight grooves, heavy riffs, and raw energy that drive every set. We turn each show into a full-on rock experience built to move the crowd from start to finish.",
  image: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO18.jpg?v=nnh253imga"
}, {
  title: "Electric Live Energy",
  description: "From hard-hitting riffs to groove-driven rock energy, we deliver a dynamic journey built on the soundtrack of our influences. Every performance is crafted to connect, blending tight musicianship, powerful tone, and the raw attitude that defines rock ’n’ roll.",
  image: "https://loving-airedale.10web.cloud/wp-content/uploads/2026/03/FOTO13.jpg?v=9hgmogxewd"
}];
function ServiceBlock({
  service,
  index
}: {
  service: (typeof servicesData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isEven = index % 2 !== 0;
  return <div data-section-id="124345" ref={ref} data-index={index} className={`flex flex-col ${isEven ? "lg:flex-row-reverse" : "lg:flex-row"} gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className="w-full lg:w-1/2 relative aspect-[4/3] lg:aspect-[3/4] max-h-[700px] overflow-hidden rounded-[4px] group">
        <img src={service.image} alt={service.title} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105" />
        {}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_20%,var(--color-background)_120%)] pointer-events-none mix-blend-multiply opacity-90" />
        <div className="absolute inset-0 shadow-[inset_0_0_100px_var(--color-background)] pointer-events-none" />
      </div>

      {}
      <div className={`w-full lg:w-1/2 flex flex-col justify-center relative py-8 lg:py-16 ${isEven ? "lg:pr-12 lg:border-r border-white/20" : "lg:pl-12 lg:border-l border-white/20"}`}>
        <h3 id="night-experience" className="text-[48px] font-serif font-normal mb-6 text-foreground tracking-tight leading-tight">
          {service.title}
        </h3>
        <p className="text-[18px] font-sans leading-relaxed text-foreground/90 font-normal mb-10">
          {service.description}
        </p>
        
        <div>
{index === 0 ? <Link to="https://www.youtube.com/playlist?list=PLi1iObtdwRn5PfAZmvkXkifbL_f-JYAql" newTab>
    <Button variant="ghost" className="rounded-[4px] bg-primary text-white border-none hover:bg-primary/90 hover:text-white transition-all duration-300 h-14 px-10 text-[16px] font-bold uppercase tracking-wider">
      checkout our setlist
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  </Link> : index === 1 ? <Link to="https://www.youtube.com/watch?v=nq27k2gTziU&list=RDnq27k2gTziU&start_radio=1" newTab>
    <Button variant="ghost" className="rounded-[4px] bg-primary text-white border-none hover:bg-primary/90 hover:text-white transition-all duration-300 h-14 px-10 text-[16px] font-bold uppercase tracking-wider">
      Live Show
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  </Link> : <Link to="https://www.facebook.com/share/v/1HmNLJr1Sm/" newTab>
    <Button variant="ghost" className="rounded-[4px] bg-primary text-white border-none hover:bg-primary/90 hover:text-white transition-all duration-300 h-14 px-10 text-[16px] font-bold uppercase tracking-wider">
      Feel the Energy
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  </Link>}
        </div>
      </div>
    </div>;
}
export default function CoreCongressServices() {
  return <section id="live-promo-reels" className="dark bg-background text-foreground py-24 lg:py-40 px-6 md:px-12 overflow-hidden relative">
      {}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-5xl mx-auto space-y-32 lg:space-y-48 relative z-10">
        {servicesData.map((service, index) => <ServiceBlock key={service.title} service={service} index={index} />)}
      </div>
    </section>;
}